import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './AccountSettings.css';

function AccountSettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [preferences, setPreferences] = useState({
    emailMarketing: true,
    emailOrderUpdates: true,
    emailPromotions: true
  });

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);

    // Check if user is a customer
    if (parsedUser.role !== 'customer') {
      navigate('/admin/dashboard');
      return;
    }

    setUser(parsedUser);
    setProfileData({
      name: parsedUser.name || '',
      email: parsedUser.email || '',
      phone: parsedUser.phone || ''
    });

    fetchPreferences();
    setLoading(false);
  }, [navigate]);

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get('/api/auth/preferences', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setPreferences({
          emailMarketing: response.data.email_marketing === 1,
          emailOrderUpdates: response.data.email_order_updates === 1,
          emailPromotions: response.data.email_promotions === 1
        });
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handlePreferenceChange = (e) => {
    setPreferences({
      ...preferences,
      [e.target.name]: e.target.checked
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.put(
        '/api/auth/profile',
        profileData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local storage
      const updatedUser = { ...user, ...profileData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update profile'
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setSaving(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      setSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.put(
        '/api/auth/password',
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update password'
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreferencesSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('authToken');
      await axios.put(
        '/api/auth/preferences',
        {
          email_marketing: preferences.emailMarketing ? 1 : 0,
          email_order_updates: preferences.emailOrderUpdates ? 1 : 0,
          email_promotions: preferences.emailPromotions ? 1 : 0
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: 'success', text: 'Preferences updated successfully!' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update preferences'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="account-settings">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="account-settings">
      <div className="account-container">
        <div className="account-sidebar">
          <div className="account-user-info">
            <div className="user-avatar">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <h3>{user?.name || 'Customer'}</h3>
            <p>{user?.email}</p>
          </div>

          <nav className="account-nav">
            <Link to="/account" className="account-nav-item">
              <span className="nav-icon">📊</span>
              Dashboard
            </Link>
            <Link to="/account/orders" className="account-nav-item">
              <span className="nav-icon">📦</span>
              Order History
            </Link>
            <Link to="/account/addresses" className="account-nav-item">
              <span className="nav-icon">📍</span>
              Addresses
            </Link>
            <Link to="/account/wishlist" className="account-nav-item">
              <span className="nav-icon">❤️</span>
              Wishlist
            </Link>
            <Link to="/account/settings" className="account-nav-item active">
              <span className="nav-icon">⚙️</span>
              Settings
            </Link>
            <button onClick={handleLogout} className="account-nav-item logout-btn">
              <span className="nav-icon">🚪</span>
              Logout
            </button>
          </nav>
        </div>

        <div className="account-main">
          <div className="account-header">
            <h1>Account Settings</h1>
            <p>Manage your account information and preferences</p>
          </div>

          {message.text && (
            <div className={`message-banner ${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Profile Information */}
          <div className="settings-section">
            <h2>Profile Information</h2>
            <form onSubmit={handleProfileSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={profileData.name}
                    onChange={handleProfileChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleProfileChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleProfileChange}
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Update Profile'}
              </button>
            </form>
          </div>

          {/* Change Password */}
          <div className="settings-section">
            <h2>Change Password</h2>
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                    minLength="8"
                  />
                  <small>At least 8 characters</small>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Update Password'}
              </button>
            </form>
          </div>

          {/* Email Preferences */}
          <div className="settings-section">
            <h2>Email Preferences</h2>
            <form onSubmit={handlePreferencesSubmit}>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="emailOrderUpdates"
                    checked={preferences.emailOrderUpdates}
                    onChange={handlePreferenceChange}
                  />
                  <span>Order Updates - Receive emails about your order status</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="emailPromotions"
                    checked={preferences.emailPromotions}
                    onChange={handlePreferenceChange}
                  />
                  <span>Promotions & Deals - Get notified about sales and special offers</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="emailMarketing"
                    checked={preferences.emailMarketing}
                    onChange={handlePreferenceChange}
                  />
                  <span>Marketing Emails - Receive updates about new products and features</span>
                </label>
              </div>

              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Update Preferences'}
              </button>
            </form>
          </div>

          {/* Danger Zone */}
          <div className="settings-section danger-zone">
            <h2>Danger Zone</h2>
            <p>Once you delete your account, there is no going back. Please be certain.</p>
            <button className="btn btn-danger">Delete Account</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountSettings;
